import type { BucketRow } from "../types";

/**
 * Build a criteria → display-label map from an aggregate series. Charts key every
 * series by its (unique) criteria string but display the shorter evaluator name;
 * this resolves that label, falling back to the criteria text when the server
 * couldn't resolve an evaluator name. Versioned evaluators (same name, different
 * criteria) can yield duplicate labels — accepted by design.
 */
export function buildCriteriaLabelMap(
  series: BucketRow[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const row of series) {
    map[row.criteria] = row.evaluator_name || row.criteria;
  }
  return map;
}

/**
 * Generic series colour palette — used for criteria lines/bars and, in the
 * grouped view, for tag-value lines (see groupColorMap). This is the single
 * teal-anchored data-viz ramp from the 016 design tokens (doc §1), legible on
 * white and consistent across every chart (replaces the old rainbow palette).
 */
// Ordered for maximal hue separation in the first ~5 slots (the common case),
// with the near-teal cyan moved LAST so it no longer sits next to the brand teal
// and gets confused with it (review v2 §1.2). First five: teal / orange / indigo
// / violet / pink.
export const CRITERIA_COLORS = [
  "#107169", // brand teal
  "#C2410C", // orange
  "#3F54B1", // indigo
  "#7C3AED", // violet
  "#BE185D", // pink
  "#15803D", // green
  "#B45309", // amber
  "#0E7490", // cyan (last — kept distinct from the brand teal)
];

/**
 * Recharts line-chart row: { date, [criteriaName]: number | null }.
 * Missing per-criteria cells are null so Recharts breaks the line at gaps.
 */
export interface ChartDataPoint {
  date: string;
  [criteriaName: string]: number | string | null;
}

/**
 * Recharts bar-chart row: one per criteria.
 */
export interface AverageDataPoint {
  criteria: string;
  average: number;
  count: number;
  stdDev?: number;
  min?: number;
  max?: number;
}

/**
 * Selectable metric for the Score Trends line chart. All are already returned
 * per bucket by /insights/aggregate, so switching between them is client-only
 * (no refetch). `avg` is the default (unchanged from before the toggle);
 * `p25`/`p10` are the lower-tail quantiles for monitoring worst cases (#1401).
 */
export type ScoreMetric = "avg" | "p25" | "p10";

export const SCORE_METRIC_OPTIONS: { key: ScoreMetric; label: string }[] = [
  { key: "avg", label: "Average" },
  { key: "p25", label: "P25" },
  { key: "p10", label: "P10" },
];

/**
 * Pivot aggregate series into Recharts line-chart rows, plotting the chosen
 * `metric` (default `avg`). A bucket missing the metric (e.g. p10/p25 absent on
 * an older response) yields a null cell so Recharts breaks the line at the gap.
 */
export function bucketRowsToLinePoints(
  series: BucketRow[],
  metric: ScoreMetric = "avg",
): {
  dataPoints: ChartDataPoint[];
  criteriaNames: string[];
} {
  const criteriaSet = new Set<string>();
  const byDate = new Map<string, Map<string, number>>();

  for (const row of series) {
    criteriaSet.add(row.criteria);
    let dateBucket = byDate.get(row.bucket_start);
    if (!dateBucket) {
      dateBucket = new Map();
      byDate.set(row.bucket_start, dateBucket);
    }
    const value = row[metric];
    if (value != null) dateBucket.set(row.criteria, value);
  }

  const criteriaNames = Array.from(criteriaSet).sort();
  const sortedDates = Array.from(byDate.keys()).sort();

  const dataPoints: ChartDataPoint[] = sortedDates.map((date) => {
    const point: ChartDataPoint = { date };
    const cells = byDate.get(date)!;
    for (const criteria of criteriaNames) {
      point[criteria] = cells.has(criteria) ? cells.get(criteria)! : null;
    }
    return point;
  });

  return { dataPoints, criteriaNames };
}

/**
 * Fitted y-axis domain for the Score Trends chart. Scores commonly cluster near
 * the top (0.85–0.95); a fixed [0, 1] squashes them into a flat strip that hides
 * real movement. Anchor the top at 1 (the ceiling — higher = better) and drop
 * the floor to the clean 0.1 tick at or below the lowest plotted point. Compute
 * in integer tenths to dodge fp noise (e.g. 0.5999999999999999). Drop an *extra*
 * tenth only when the low point would otherwise sit on the axis (within 0.05),
 * so we neither clip a line nor leave a big empty band — min 0.39 → 0.3,
 * min 0.40 → 0.3, min 0.85 → 0.8. Falls back to [0, 1] when there's no data.
 */
export function computeScoreYDomain(
  dataPoints: ChartDataPoint[],
  criteriaNames: string[],
): [number, number] {
  let min = Infinity;
  for (const point of dataPoints) {
    for (const criteria of criteriaNames) {
      const value = point[criteria];
      if (typeof value === "number" && Number.isFinite(value)) {
        min = Math.min(min, value);
      }
    }
  }
  if (!Number.isFinite(min)) return [0, 1];
  // Do the rounding in integer hundredths ("cents") so float noise can't tip a
  // boundary — e.g. 0.85 is really 0.8499…, and `0.85 - 0.8 < 0.05` would wrongly
  // read true. cents = the score to the nearest 0.01; floorTenths = its 0.1 tick.
  const cents = Math.round(min * 100); // 0.85 → 85, 0.39 → 39
  let floorTenths = Math.floor(cents / 10); // → the 0.1 gridline below the point
  if (cents - floorTenths * 10 < 5) floorTenths -= 1; // on/near the tick → drop one
  return [Math.max(0, floorTenths) / 10, 1];
}

/**
 * One criterion's score histogram — per-bin counts summed across every bucket in
 * the selected range (a normal distribution histogram, not a time series).
 */
export interface HistogramSeries {
  criteria: string;
  counts: number[]; // per-bin totals, length === binCount
  total: number; // Σ counts (evaluations for this criterion in range)
}

/**
 * Collapse the (ungrouped, histogram=true) series into one histogram per
 * criterion by element-wise summing each bucket's per-bin counts. The time axis
 * is dropped — the selected date range scopes the period. Rows without a
 * histogram are skipped (defensive — shouldn't occur when histogram=true was
 * requested); criteria are sorted.
 */
export function bucketRowsToHistograms(series: BucketRow[]): HistogramSeries[] {
  const byCriteria = new Map<string, number[]>();

  for (const row of series) {
    if (!row.histogram) continue;
    const acc = byCriteria.get(row.criteria);
    if (!acc) {
      byCriteria.set(row.criteria, [...row.histogram]);
    } else {
      row.histogram.forEach((c, i) => {
        acc[i] = (acc[i] ?? 0) + c;
      });
    }
  }

  return Array.from(byCriteria.keys())
    .sort()
    .map((criteria) => {
      const counts = byCriteria.get(criteria)!;
      return { criteria, counts, total: counts.reduce((s, c) => s + c, 0) };
    });
}

/**
 * One faceted line chart per criterion: lines are tag values, x is the bucket.
 */
export interface GroupedLineFacet {
  criteria: string;
  dataPoints: ChartDataPoint[];
  groupNames: string[]; // sorted distinct group values in this facet
}

/**
 * Pivot a grouped aggregate series into one facet per criterion, with one line
 * per tag value. The server caps criteria (≤10) and groups, so this renders
 * exactly what it returns — no client-side cap. Rows without a group_value
 * (untagged / ungrouped) are skipped.
 */
export function bucketRowsToGroupedLinePoints(
  series: BucketRow[],
): GroupedLineFacet[] {
  // criteria -> { groups, byDate: bucket_start -> (group_value -> avg) }
  const byCriteria = new Map<
    string,
    { groups: Set<string>; byDate: Map<string, Map<string, number>> }
  >();

  for (const row of series) {
    if (row.group_value == null) continue;
    let facet = byCriteria.get(row.criteria);
    if (!facet) {
      facet = { groups: new Set(), byDate: new Map() };
      byCriteria.set(row.criteria, facet);
    }
    facet.groups.add(row.group_value);
    let dateBucket = facet.byDate.get(row.bucket_start);
    if (!dateBucket) {
      dateBucket = new Map();
      facet.byDate.set(row.bucket_start, dateBucket);
    }
    dateBucket.set(row.group_value, row.avg);
  }

  return Array.from(byCriteria.keys())
    .sort()
    .map((criteria) => {
      const { groups, byDate } = byCriteria.get(criteria)!;
      const groupNames = Array.from(groups).sort();
      const sortedDates = Array.from(byDate.keys()).sort();
      const dataPoints: ChartDataPoint[] = sortedDates.map((date) => {
        const point: ChartDataPoint = { date };
        const cells = byDate.get(date)!;
        for (const group of groupNames) {
          point[group] = cells.has(group) ? cells.get(group)! : null;
        }
        return point;
      });
      return { criteria, dataPoints, groupNames };
    });
}

/**
 * Map each group value to a stable colour from CRITERIA_COLORS, so a tag value
 * keeps the same colour across every criterion facet and the shared legend.
 */
export function groupColorMap(allGroupValues: string[]): Record<string, string> {
  const sorted = Array.from(new Set(allGroupValues)).sort();
  const map: Record<string, string> = {};
  sorted.forEach((value, i) => {
    map[value] = CRITERIA_COLORS[i % CRITERIA_COLORS.length];
  });
  return map;
}

/**
 * Collapse the aggregate series into one bar-chart row per criteria.
 * - count   = Σ count
 * - average = count-weighted mean of bucket avgs
 * - min/max = min of mins / max of maxes
 * - stdDev  = pooled sample stddev across buckets; per-bucket stddev=null is treated
 *            as 0 (single-row buckets contribute zero within-bucket variance).
 *            Returns undefined when the overall count is <= 1.
 */
export function bucketRowsToBarPoints(series: BucketRow[]): AverageDataPoint[] {
  const grouped = new Map<string, BucketRow[]>();
  for (const row of series) {
    const bucketList = grouped.get(row.criteria);
    if (bucketList) {
      bucketList.push(row);
    } else {
      grouped.set(row.criteria, [row]);
    }
  }

  const result: AverageDataPoint[] = [];
  for (const [criteria, buckets] of grouped.entries()) {
    const totalCount = buckets.reduce((sum, b) => sum + b.count, 0);
    if (totalCount === 0) continue;

    const weightedSum = buckets.reduce((sum, b) => sum + b.avg * b.count, 0);
    const average = weightedSum / totalCount;
    const min = buckets.reduce((m, b) => Math.min(m, b.min), buckets[0].min);
    const max = buckets.reduce((m, b) => Math.max(m, b.max), buckets[0].max);

    let stdDev: number | undefined;
    if (buckets.length === 1) {
      stdDev = buckets[0].stddev ?? undefined;
    } else if (totalCount > 1) {
      let numerator = 0;
      for (const b of buckets) {
        const s = b.stddev ?? 0;
        numerator += (b.count - 1) * s * s + b.count * (b.avg - average) ** 2;
      }
      stdDev = Math.sqrt(numerator / (totalCount - 1));
    }

    result.push({ criteria, average, count: totalCount, stdDev, min, max });
  }

  return result.sort((a, b) => a.criteria.localeCompare(b.criteria));
}

/**
 * One heatmap cell: the count-weighted average score for a (criteria, tag value)
 * pair, collapsed across all time buckets.
 */
export interface HeatmapCell {
  criteria: string;
  group: string;
  avg: number;
  count: number;
  /**
   * Evaluations in this cell scoring below the requested `below_threshold`,
   * summed across its buckets. Present only when the response carried
   * `below_count` (the request set `below_threshold`); it is what lets triage
   * judge a cell on how many of its results failed rather than on where its mean
   * landed. Counted server-side, so it is exact rather than read off bin edges.
   */
  belowCount?: number;
}

/** Running sum of a nullable per-bucket `below_count`; undefined until one arrives. */
function addBelowCount(
  acc: number | undefined,
  row: number | null | undefined,
): number | undefined {
  if (row == null) return acc;
  return (acc ?? 0) + row;
}

/**
 * Heatmap grid derived from a grouped aggregate series:
 * - `criteria` — sorted row labels (≤10, server-capped)
 * - `groups`   — sorted union of column labels (≤10)
 * - `cells`    — keyed by `heatmapCellKey`; a (criteria, group) pair with no data
 *               is ABSENT (never synthesised as score 0).
 */
export interface HeatmapData {
  criteria: string[];
  groups: string[];
  cells: Map<string, HeatmapCell>;
}

/** NUL-joined cell key so free-form criteria/group strings can't collide. */
export function heatmapCellKey(criteria: string, group: string): string {
  return `${criteria} ${group}`;
}

/**
 * Collapse a grouped aggregate series into one cell per (criteria, tag value),
 * dropping the time axis. The cell score is the count-weighted mean of the
 * bucket averages (same weighting as bucketRowsToBarPoints). Rows without a
 * group_value (untagged) are skipped; an empty-string group is kept as its own
 * column. The server caps criteria and groups, so this renders exactly what it
 * returns — no client-side cap.
 */
export function bucketRowsToHeatmapCells(series: BucketRow[]): HeatmapData {
  const acc = new Map<
    string,
    {
      criteria: string;
      group: string;
      weightedSum: number;
      count: number;
      belowCount?: number;
    }
  >();
  const criteriaSet = new Set<string>();
  const groupSet = new Set<string>();

  for (const row of series) {
    if (row.group_value == null) continue;
    const group = row.group_value;
    criteriaSet.add(row.criteria);
    groupSet.add(group);
    const key = heatmapCellKey(row.criteria, group);
    let cell = acc.get(key);
    if (!cell) {
      cell = { criteria: row.criteria, group, weightedSum: 0, count: 0 };
      acc.set(key, cell);
    }
    cell.weightedSum += row.avg * row.count;
    cell.count += row.count;
    cell.belowCount = addBelowCount(cell.belowCount, row.below_count);
  }

  const cells = new Map<string, HeatmapCell>();
  for (const [key, c] of acc.entries()) {
    if (c.count === 0) continue;
    cells.set(key, {
      criteria: c.criteria,
      group: c.group,
      avg: c.weightedSum / c.count,
      count: c.count,
      belowCount: c.belowCount,
    });
  }

  return {
    criteria: Array.from(criteriaSet).sort(),
    groups: Array.from(groupSet).sort(),
    cells,
  };
}

/**
 * A HeatmapData with no columns — one count-weighted average per criterion, keyed
 * under the empty group. Feeds HeatmapChart's scorecard fallback so the heatmap
 * card renders per-criterion bars by default (before any group-by is chosen)
 * instead of nothing. Built from the ungrouped aggregate series.
 */
export function bucketRowsToUngroupedHeatmap(series: BucketRow[]): HeatmapData {
  const bars = bucketRowsToBarPoints(series);
  // Per-criterion failure counts summed across buckets, carried onto the cell so
  // triage can count failures instead of inferring them from the mean.
  const belowCounts = new Map<string, number>();
  for (const row of series) {
    if (row.below_count == null) continue;
    belowCounts.set(
      row.criteria,
      (belowCounts.get(row.criteria) ?? 0) + row.below_count,
    );
  }
  const cells = new Map<string, HeatmapCell>();
  for (const bar of bars) {
    cells.set(heatmapCellKey(bar.criteria, ""), {
      criteria: bar.criteria,
      group: "",
      avg: bar.average,
      count: bar.count,
      belowCount: belowCounts.get(bar.criteria),
    });
  }
  return { criteria: bars.map((b) => b.criteria), groups: [], cells };
}
